import { collection, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc, where } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { RoomToken } from "../models/RoomToken";
import { Util } from "./Utils/Util";
import type { FireClient } from "./FireClient";
import { getFunc } from "./FireClient";

/**
 * FireClientToken - Room API Token CRUD 서비스
 *
 * Firestore 경로: roomtokens/{tokenId} (루트 컬렉션, roomId 인덱스로 방별 조회)
 */
export class FireClientToken {
  private fire: FireClient;

  constructor(fire: FireClient) {
    this.fire = fire;
  }

  /**
   * 토큰 목록 실시간 구독 (roomId로 필터링)
   */
  subscribeToRoomTokens(roomId: string, handler: (tokens: RoomToken[]) => void) {
    const q = query(
      collection(this.fire.store, RoomToken.COL),
      where('roomId', '==', roomId)
    );
    return onSnapshot(q, (snap) => {
      const tokens = snap.docs.map(d => ({ ...d.data(), id: d.id } as RoomToken));
      handler(tokens.sort((a, b) => {
        const da = a.created instanceof Date ? a.created : new Date(a.created);
        const db2 = b.created instanceof Date ? b.created : new Date(b.created);
        return da.getTime() - db2.getTime();
      }));
    });
  }

  /**
   * 토큰 생성
   * @returns 평문 시크릿 키 (한 번만 표시!)
   */
  async createToken(
    roomId: string,
    name: string,
    createdBy: string,
    photoUrl: string | null = null,
    scopes: string[] = ['message:send'],
    filesVisibility: 'all' | 'selected' = 'all',
    memosVisibility: 'all' | 'selected' = 'all',
    visibleFileIds: string[] = [],
    visibleMemoIds: string[] = []
  ): Promise<{ token: RoomToken; plainSecret: string }> {
    // 1. 평문 키 생성
    const plainSecret = RoomToken.generateSecret();
    // 2. SHA-256 해시
    const secretHash = await RoomToken.hashSecret(plainSecret);
    // 3. 토큰 객체 생성
    const token = new RoomToken(name, roomId, createdBy, secretHash);
    token.photoUrl = photoUrl;
    token.scopes = scopes;
    token.filesVisibility = filesVisibility;
    token.memosVisibility = memosVisibility;
    token.visibleFileIds = visibleFileIds;
    token.visibleMemoIds = visibleMemoIds;

    // 4. Firestore 저장 (루트 컬렉션)
    await setDoc(
      doc(this.fire.store, RoomToken.COL, token.id),
      Util.pojo(token)
    );

    return { token, plainSecret };
  }

  /**
   * 토큰 정보 수정
   */
  async updateToken(roomId: string, tokenId: string, data: Partial<Pick<RoomToken, 'name' | 'photoUrl' | 'isActive' | 'scopes' | 'filesVisibility' | 'memosVisibility' | 'visibleFileIds' | 'visibleMemoIds' | 'allowedIps' | 'monthlyQuota'>>): Promise<void> {
    await updateDoc(
      doc(this.fire.store, RoomToken.COL, tokenId),
      Util.pojo(data)
    );
  }

  /**
   * 토큰 시크릿 키 재발급
   * 새 평문 키를 생성하고 해시만 Firestore에 저장
   * @returns 새 평문 시크릿 키 (한 번만 표시!)
   */
  async regenerateSecret(tokenId: string): Promise<string> {
    const plainSecret = RoomToken.generateSecret();
    const secretHash = await RoomToken.hashSecret(plainSecret);
    await updateDoc(
      doc(this.fire.store, RoomToken.COL, tokenId),
      { secretHash }
    );
    return plainSecret;
  }

  /**
   * 토큰 삭제 (+ 관련 메시지 일괄 삭제 → Cloud Function 경유)
   */
  async deleteToken(roomId: string, tokenId: string): Promise<{ messagesDeleted: number }> {
    const func = getFunc('deleteRoomTokenAndMessages');
    const result = await func({ roomId, tokenId });
    return result.data as { messagesDeleted: number };
  }

  /**
   * 프로필 이미지 업로드
   * @returns 다운로드 URL
   */
  async uploadTokenProfileImage(roomId: string, tokenId: string, file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `rooms/${roomId}/tokens/${tokenId}/profile.${ext}`;
      const storage = getStorage();
      const storageRef = ref(storage, path.normalize('NFC'));
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        () => {},
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          // 토큰에 photoUrl 업데이트
          await this.updateToken(roomId, tokenId, { photoUrl: url });
          resolve(url);
        }
      );
    });
  }
}
